// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';
import {
  isAuthActionPath,
  scrubActionCodeFromBreadcrumb,
  scrubActionCodeFromEvent,
} from 'utils/shared/sentryPrivacy';

const SENTRY_DSN = process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN;

const isDeveloperDocs = () =>
  typeof window !== 'undefined' &&
  /^\/docs(?:$|\/(?:api|mcp)(?:\/|$))/.test(window.location.pathname);

// Firebase account links hold a one-time code in the URL. Errors there still report, without the
// code; a performance trace would record the full URL, so the page takes none.
const isAuthAction = () =>
  typeof window !== 'undefined' && isAuthActionPath(window.location.pathname);

Sentry.init({
  enabled: !isDeveloperDocs(),
  beforeSend: (event) => (isDeveloperDocs() ? null : scrubActionCodeFromEvent(event)),
  beforeBreadcrumb: scrubActionCodeFromBreadcrumb,
  dsn: SENTRY_DSN,
  environment: process.env.APP_STAGE,
  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampler: () => (isDeveloperDocs() || isAuthAction() ? 0 : 1),
  // ...
  // Note: if you want to override the automatic release value, do not set a
  // `release` value here - use the environment variable `SENTRY_RELEASE`, so
  // that it will also get attached to your source maps
  //
  //
  ignoreErrors: [
    // InsiderFinance error flood:
    'Could not verify JWT: JWTExpired',
    'Failed to fetch',
    'AbortError: The user aborted a request',
    "NotAllowedError: play() failed because the user didn't interact with the document first",
    'NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',

    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
  ],
  // @ts-ignore, available in web
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
});
